// Casino Dashboard › WOF Transactions
//
// Ported from casino_dashboard/src/layouts/woftransactions — Wheel of Fortune
// claims from GET /dashboard/wofPlayers, with PDF and CSV exports.
import React from "react";
import { Box, Button, Snackbar, Alert } from "@mui/material";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import DataTable from "../../components/DataTable";
import DashboardService from "../../Utility/dashboardService";
import { pill } from "../../components/tableKit";
import { StateLine, errText, fixed } from "./shared";

const columns = [
  { header: "Date", accessor: "created_on", align: "left" },
  { header: "User Id", accessor: "id_user", align: "left" },
  { header: "Name", accessor: "name", align: "center" },
  { header: "Email", accessor: "email", align: "center" },
  {
    header: "Claim",
    accessor: "is_claim",
    align: "center",
    render: (v) => pill(v === "Yes" ? "Yes" : "No", v === "Yes" ? "ok" : "neutral"),
  },
  { header: "Coin", accessor: "sc_claim", align: "center" },
  { header: "Coin Type", accessor: "coin_type", align: "center" },
  { header: "Multiplier", accessor: "multiplier", align: "center" },
  { header: "Total Coins", accessor: "profit", align: "center" },
  { header: "Min Package", accessor: "min_package", align: "center" },
];

export default function CasinoWofTransactions() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [downloading, setDownloading] = React.useState(null);
  const [snack, setSnack] = React.useState(null);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setRows([]);
    try {
      const result = await DashboardService.getData("/dashboard/wofPlayers");
      setRows(Array.isArray(result?.data) ? result.data : []);
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const download = async (code) => {
    setDownloading(code);
    try {
      await DashboardService.downloadFile(`/dashboard/wofPlayers/${code}`, `wofPlayers.${code}`);
      setSnack({ text: `${code.toUpperCase()} downloaded.`, sev: "success" });
    } catch (e) {
      setSnack({ text: errText(e), sev: "error" });
    } finally {
      setDownloading(null);
    }
  };

  const tableRows = React.useMemo(
    () =>
      rows.map((t) => ({
        created_on: t.created_on,
        id_user: t.id_user,
        name: t.name,
        email: t.email,
        is_claim: t.is_claim ? "Yes" : "No",
        sc_claim: fixed(t.sc_claim),
        coin_type: t.coin_type,
        multiplier: `${t.multiplier}x`,
        profit: fixed(Number(t.multiplier) * Number(t.sc_claim)),
        min_package: fixed(t.min_package),
      })),
    [rows]
  );

  return (
    <Box>
      <PageHeader
        title="WOF Transactions"
        crumb="Casino Dashboard / WOF Transactions"
        subtitle="Wheel of Fortune claims and the coins they awarded"
        actions={
          <>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<FileDownloadRoundedIcon />}
              onClick={() => download("pdf")}
              disabled={!!downloading}
            >
              {downloading === "pdf" ? "Downloading…" : "PDF"}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<FileDownloadRoundedIcon />}
              onClick={() => download("csv")}
              disabled={!!downloading}
            >
              {downloading === "csv" ? "Downloading…" : "CSV"}
            </Button>
          </>
        }
      />

      <SectionCard title="Transactions" sx={{ p: 2, height: "auto" }}>
        <StateLine error={error} />
        {!error && (
          <DataTable
            columns={columns}
            rows={tableRows}
            loading={loading}
            searchable
            sortable
            paginated
            entriesPerPage
            showTotalEntries
            searchPlaceholder="Search name, email, user id…"
            emptyText="No Wheel of Fortune claims found."
          />
        )}
      </SectionCard>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack?.sev || "success"} onClose={() => setSnack(null)} variant="filled">
          {snack?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
